import { Component, EventEmitter, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import * as moment from 'moment';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import { InputSummary, InputSummaryItem } from '../../../models/input-summary';
import { Project } from '../../../models/project/project.model';
import { PeriodUnitMapper } from '../../../results/results-util';
import Utils from '../../../util';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

export class InputReviewData {
  titleText: string[];
  confirmText: string[];
  tableHeaders: string[];
  tableData: any[];
  isResults?: boolean;
  results?: any;
}

@Component({
  selector: 'app-input-review-dialog',
  templateUrl: './input-review-dialog.component.html',
  styleUrls: [
    './input-review-dialog.component.scss',
  ]
})
export class InputReviewDialogComponent extends UserpilotReloader implements OnDestroy {

  onDestroyNotifier = new EventEmitter<boolean>();

  trigger: string;

  isSimulating = false;
  currencyUnitsLoaded = false;
  oldResults = true;
  resultsLoaded = false;
  validationAccepted = false;
  showTable = false;

  listIndex = 0;

  titleText: string;
  confirmText: string;

  selectedPeriodType = 'year';
  selectedTechUnitType = 'metric';
  selectedCurrency: Unit;

  exchangeRateDate: string;

  periodCategories: DropdownDisplayItem[] = [];
  techUnitTypes: DropdownDisplayItem[] = [];
  currencyUnits: Unit[] = [];

  processedTableData: InputSummaryItem[][] = [];
  processedResultsTableData: InputSummaryItem[] = [];
  tableData: InputSummaryItem[] = [];
  tableHeaders: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) private inputData: InputReviewData,
    private dialogRef: MatDialogRef<InputReviewDialogComponent>,
    private unitService: UnitService,
    private projectCommService: ProjectCommunicationService,
    private toastrService: ConfiguredToastrService,
    public translationService: AnessaTranslationService,
  ) {
    super();
    this.trigger = this.translationService.getCurrentLang();

    // Process data on load to speed up navigating between lists
    if (this.inputData.isResults) {
      this.processResults(this.inputData.results);
      this.resultsLoaded = true;
    } else {
      this.processData(this.inputData.tableData);
    }
    this.setupDropdownData();
  }

  ngOnDestroy() {
    this.onDestroyNotifier.next(true);
    this.onDestroyNotifier.unsubscribe();
  }

  private setupDropdownData() {
    this.periodCategories = [
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.DAY'), 'day'),
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.MONTH'), 'month'),
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.YEAR'), 'year')
    ];

    this.techUnitTypes = [
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.METRIC'), 'metric'),
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.IMPERIAL'), 'imperial')
    ];

    this.unitService.getCurrencyUnits().then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyUnits = units;

        this.currencyUnits.sort((first: Unit, second: Unit) => Number(first.order) - Number(second.order));
        this.currencyUnitsLoaded = true;

        // Select USD by default
        this.selectedCurrency = this.currencyUnits.find(currency => currency.displayName === 'USD');
        this.setTableData();
      }
    );
  }

  /**
   * Recursive function to gather all inputs in the passed input summary
   */
  gatherSubsections(outputData: InputSummaryItem[], subSection: any, path: string[] = []) {
    for (const key of Object.keys(subSection)) {
      const item = subSection[key] as InputSummary;
      if (item.inputs && item.inputs.length > 0) {
        item.inputs.forEach(input => {
          input.name = [
            ...path,
            key,
            input.name,
          ].filter(item => item !== '').join(', ');
          outputData.push(input);
        });
      }
      if (item.subSection && Object.keys(item.subSection).length > 0) {
        this.gatherSubsections(outputData, item.subSection, [...path, key]);
      }
    }
  }

  /**
   * Replace unit ids with display name
   */
  replaceValues(tableData: InputSummaryItem[]) {
    for (const item of tableData) {
      if (item.unit) {
        const unit = this.unitService.getUnitById(item.unit);
        let translatedDisplayName = this.translationService.get(unit.translationId);

        // Convert any /day values into /month or /year
        if (unit.displayName.includes('/day')) {
          const unitTranslationHack = PeriodUnitMapper[unit.id];
          if (!unitTranslationHack) {
            console.log('add this to the unit hack: ' + unit.id + ' - ' + unit.displayName);
          }
          let periodTranslation = this.translationService.get('RESULTS.GENERAL.DAY');
          if (this.selectedPeriodType === 'month') {
            periodTranslation = this.translationService.get('RESULTS.GENERAL.MONTH');
            item.value = String(Number(item.value) * 30);
          } else if (this.selectedPeriodType === 'year') {
            periodTranslation = this.translationService.get('RESULTS.GENERAL.YEAR');
            item.value = String(Number(item.value) * 365);
          }
          translatedDisplayName = this.translationService.get(unitTranslationHack, {period: periodTranslation});
        }
        item.unit = Unit.parseUnitDisplayName(translatedDisplayName);
      } else {
        item.unit = '-';
      }
    }
  }

  /**
   * Returns the metric unit counterpart in the imperial system
   * @param unitId unit id
   */
  private getImperialCounterpart(unitId: string): Unit {
    let imperialUnitId = '';
    switch (unitId) {
      case '000000000000000000004901': // ton/day
        imperialUnitId = '000000000000000000004902'; // short ton/day
        break;
      case '000000000000000000003803': // m3/day
        imperialUnitId = '000000000000000000003806'; // ft3/day
        break;
      case '000000000000000000003805': // Nm3/day
        imperialUnitId = '000000000000000000003804'; // scf/day
        break;
      case '000000000000000000005201': // kg CO2eq/day
        imperialUnitId = '000000000000000000005203'; // lbs CO2eq/day
        break;
      case '000000000000000000006501': // litre/day
        imperialUnitId = '000000000000000000006502'; // gal. US/day
        break;
      case '000000000000000000001302': // day
      case '000000000000000000001304': // month
      case '000000000000000000001305': // year
      case '000000000000000000003708': // kWh/day
      case '000000000000000000000401': // %
        return null;
      default:
        imperialUnitId = unitId;
    }
    return this.unitService.getUnitById(imperialUnitId);
  }

  /**
   * Creates a deep copy of the given table data and converts units to selected
   * units
   * @returns A deep copy of the table data with data converted to selected units
   */
  convertUnits(tableData: InputSummaryItem[]) {
    const convertedData: InputSummaryItem[] = [];

    for (const row of tableData) {
      const newData = new InputSummaryItem(row);
      const currentUnit = this.unitService.getUnitById(newData.unit);
      // Don't attempt to convert non-numeric values
      if (!currentUnit) {
        convertedData.push(newData);
        continue;
      }
      // Don't convert units if results are not loaded or row value is NaN
      if (!this.resultsLoaded || isNaN(Number(newData.value))) {
        newData.unit = currentUnit.id;
        convertedData.push(newData);
        continue;
      }

      // Convert if period unit
      if (currentUnit.unitType === '000000000000000000000013') {
        const periodUnit = this.unitService.getUnitsByUnitTypeId('000000000000000000000013').find(unit => unit.name === this.selectedPeriodType);
        newData.value = String(Unit.convertValue(Number(newData.value), currentUnit.id, periodUnit.id));
        newData.unit = periodUnit.id;
        convertedData.push(newData);
        continue;
      }

      // Convert if currency unit
      if (currentUnit.unitType === this.selectedCurrency.unitType) {
        newData.value = String(Unit.convertValue(Number(newData.value), currentUnit.id, this.selectedCurrency.id));
        newData.unit = this.selectedCurrency.id;
        convertedData.push(newData);
        continue;
      }

      // Convert if equivalent imperial unit found
      const imperialUnit = this.getImperialCounterpart(newData.unit);
      if (this.selectedTechUnitType === 'imperial' && imperialUnit) {
        newData.value = String(Unit.convertValue(Number(newData.value), currentUnit.id, imperialUnit.id));
        newData.unit = imperialUnit.id;
      }
      convertedData.push(newData);
    }

    return convertedData;
  }

  setTableData() {
    this.tableHeaders = this.inputData.tableHeaders;

    if (this.resultsLoaded) {
      this.titleText = 'PROJECT.INITIAL.RESULTS';
      this.tableData = this.processedResultsTableData;
    } else {
      this.titleText = this.inputData.titleText[this.listIndex];
      this.confirmText = this.inputData.confirmText[this.listIndex];
      this.tableData = this.processedTableData[this.listIndex];
    }
    this.tableData = this.convertUnits(this.tableData);
    this.replaceValues(this.tableData);
    this.translateFields(this.tableData);

    this.showTable = true;
  }

  processData(tableDataList: any[]) {
    for (const tableData of tableDataList) {
      const processedData = [];
      this.gatherSubsections(processedData, tableData);
      this.processedTableData.push(processedData);
    }
  }

  translateFields(tableData: InputSummaryItem[]) {
    for (const row of tableData) {
      row[this.translationService.get('RESULTS.GENERAL.NAME')] = row.name;
      row[this.translationService.get('RESULTS.GENERAL.VALUE')] = row.value;
      row[this.translationService.get('RESULTS.GENERAL.UNIT')] = row.unit;
    }
  }

  /**
   * Extract desired results from results object
   */
  processResults(results: any) {
    const processedData = [];

    // Economic metrics
    const economicMetrics = results['RESULTS.DATA.FINANCIAL']['RESULTS.DATA.EAFM']['RESULTS.DATA.ECONOMIC_METRICS.TITLE'];

    // Simple Payback Period
    processedData.push(
      new InputSummaryItem({
        name: this.translationService.get(economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.SPP']['RESULTS.DATA.LABEL']),
        unit: economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.SPP']['RESULTS.DATA.UNIT'],
        value: String(economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.SPP']['RESULTS.DATA.VALUES'][0]),
        id: economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.SPP']['RESULTS.DATA.LABEL'],
      })
    );
    // NPV
    processedData.push(
      new InputSummaryItem({
        name: this.translationService.get(economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.NPV']['RESULTS.DATA.LABEL']),
        unit: economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.NPV']['RESULTS.DATA.UNIT'],
        value: String(economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.NPV']['RESULTS.DATA.VALUES'][0]),
        id: economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.NPV']['RESULTS.DATA.LABEL'],
      })
    );
    // IRR
    processedData.push(
      new InputSummaryItem({
        name: this.translationService.get(economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.IRR']['RESULTS.DATA.LABEL']),
        unit: economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.IRR']['RESULTS.DATA.UNIT'],
        value: String(economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.IRR']['RESULTS.DATA.VALUES'][0]),
        id: economicMetrics['RESULTS.DATA.ECONOMIC_METRICS.IRR']['RESULTS.DATA.LABEL'],
      })
    );

    // Feedstock input
    // FU if wet, FUPB if dry
    if (this.projectCommService.getResultProjectState().plantSpecifications.isDryBatch()) {
      const feedstockInput = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.GENERAL']['RESULTS.DATA.TECHNICAL.DIF']['RESULTS.DATA.TECHNICAL.FUPB'];
      processedData.push(
        new InputSummaryItem({
          name: this.translationService.get(feedstockInput['RESULTS.DATA.LABEL']),
          unit: feedstockInput['RESULTS.DATA.UNIT'],
          value: String(feedstockInput['RESULTS.DATA.VALUES'][0]),
          id: feedstockInput['RESULTS.DATA.LABEL'],
        })
      );
    } else {
      const feedstockInput = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.GENERAL']['RESULTS.DATA.TECHNICAL.DIF']['RESULTS.DATA.TECHNICAL.FU'];
      processedData.push(
        new InputSummaryItem({
          name: this.translationService.get(feedstockInput['RESULTS.DATA.LABEL']),
          unit: feedstockInput['RESULTS.DATA.UNIT'],
          value: String(feedstockInput['RESULTS.DATA.VALUES'][0]),
          id: feedstockInput['RESULTS.DATA.LABEL'],
        })
      );
    }

    // Biogas production
    const biogasProduction = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.GENERAL']['RESULTS.DATA.TECHNICAL.DOF']['RESULTS.DATA.TECHNICAL.BP'];
    processedData.push(
      new InputSummaryItem({
        name: this.translationService.get(biogasProduction['RESULTS.DATA.LABEL']),
        unit: biogasProduction['RESULTS.DATA.UNIT'],
        value: String(biogasProduction['RESULTS.DATA.VALUES'][0]),
        id: biogasProduction['RESULTS.DATA.LABEL'],
      })
    );

    // Digester output
    // DFR if wet, TSDPR if dry
    if (this.projectCommService.getResultProjectState().plantSpecifications.isDryBatch()) {
      const digesterOutput = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.GENERAL']['RESULTS.DATA.TECHNICAL.DOF']['RESULTS.DATA.TECHNICAL.TSDPR'];
      processedData.push(
        new InputSummaryItem({
          name: this.translationService.get(digesterOutput['RESULTS.DATA.LABEL']),
          unit: digesterOutput['RESULTS.DATA.UNIT'],
          value: String(digesterOutput['RESULTS.DATA.VALUES'][0]),
          id: digesterOutput['RESULTS.DATA.LABEL'],
        })
      );
    } else {
      const digesterOutput = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.GENERAL']['RESULTS.DATA.TECHNICAL.DOF']['RESULTS.DATA.TECHNICAL.DFR'];
      processedData.push(
        new InputSummaryItem({
          name: this.translationService.get(digesterOutput['RESULTS.DATA.LABEL']),
          unit: digesterOutput['RESULTS.DATA.UNIT'],
          value: String(digesterOutput['RESULTS.DATA.VALUES'][0]),
          id: digesterOutput['RESULTS.DATA.LABEL'],
        })
      );
    }

    // Estimated GHG reduction
    if (this.projectCommService.getResultProjectState().projectInfo.useGHG) {
      const ghgEmissions = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.GHGE']['RESULTS.DATA.TECHNICAL.GHG']['RESULTS.DATA.TECHNICAL.EGHGR'];
      processedData.push(
        new InputSummaryItem({
          name: this.translationService.get(ghgEmissions['RESULTS.DATA.LABEL']),
          unit: ghgEmissions['RESULTS.DATA.UNIT'],
          value: String(ghgEmissions['RESULTS.DATA.VALUES'][0]),
          id: ghgEmissions['RESULTS.DATA.LABEL'],
        })
      );
    }

    // RNG
    const sellingProducts = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.PPD']['RESULTS.DATA.TECHNICAL.SP'];
    if ('RESULTS.DATA.TECHNICAL.RNGP' in sellingProducts) {
      const rng = sellingProducts['RESULTS.DATA.TECHNICAL.RNGP'];
      processedData.push(
        new InputSummaryItem({
          name: this.translationService.get(rng['RESULTS.DATA.LABEL']),
          unit: rng['RESULTS.DATA.UNIT'],
          value: String(rng['RESULTS.DATA.VALUES'][0]),
          id: rng['RESULTS.DATA.LABEL'],
        })
      );
    }

    // Gross products
    const grossProducts = results['RESULTS.DATA.TECHNICAL.TITLE']['RESULTS.DATA.TECHNICAL.PPD']['RESULTS.DATA.TECHNICAL.GP'];
    for (const product of Object.keys(grossProducts)) {
      processedData.push(
        new InputSummaryItem({
          name: this.translationService.get(grossProducts[product]['RESULTS.DATA.LABEL']),
          unit: grossProducts[product]['RESULTS.DATA.UNIT'],
          value: String(grossProducts[product]['RESULTS.DATA.VALUES'][0]),
          id: grossProducts[product]['RESULTS.DATA.LABEL'],
        })
      );
    }

    this.processedResultsTableData = processedData;
  }

  modifiyField(project: Project, name: string, path: any[], value: number) {
    let accessor = project;
    let key = path[0];
    for (let i = 0; i < path.length - 1; i++) {
      key = path[i];
      if (Array.isArray(accessor)) {
        accessor = accessor[Number(key)];
      } else if (accessor instanceof Object) {
        accessor = accessor[key];
      }
    }
    key = path[path.length - 1];

    let fieldsModified = 0;
    if (accessor[key] !== value) {
      // Update value in project and processed data
      const rawItem = this.processedTableData[this.listIndex].find(item => item.name === name);
      rawItem.value = String(value);
      accessor[key] = value;
      fieldsModified +=  1;
    }
    return fieldsModified;
  }

  updateAndSaveProject(items: InputSummaryItem[]) {
    const project = this.projectCommService.project;
    let fieldsModified = 0;
    for (const item of items) {
      if (item.value == null) {
        throw new Error('PROJECT.INITIAL.REVIEW_DIALOG.ERRORS.MISSING_VALUE');
      }
      // Only allow numerical values to be edited
      if (item.value != null && !isNaN(Number(item.value)) && item.path && item.path.length > 0) {
        fieldsModified += this.modifiyField(project, item.name, item.path, Number(item.value));
      }
    }

    // Check that biogas share data does not exceed 100%
    let totalSum = 0;
    project.plantSpecifications.biogasShareData.forEach(share => {
      totalSum += share.share.value;
    });
    if (totalSum !== 100) {
      throw new RangeError('PROJECT.INITIAL.REVIEW_DIALOG.ERRORS.BIOGAS_SHARE_NOT_100');
    }

    if (fieldsModified > 0) {
      // Save the project if any fields were modified
      this.projectCommService.enableProjectSaveToDatabase();
      this.projectCommService.projectSavedNotifier
        .pipe(takeUntil(this.onDestroyNotifier))
        .pipe(takeWhile(x => !x, true))
        .subscribe(saved => {
          if (saved) {
            this.projectCommService.disableProjectSaveToDatabase();
          }
        });
      this.oldResults = true;
      this.projectCommService.saveProject();
    }
  }

  onApproveClick() {
    if (this.limitToScreeningProjects()) return;
    this.dialogRef.close(true);
  }

  onNextClick() {
    if (!this.resultsLoaded) {
      try {
        this.updateAndSaveProject(this.tableData);
      } catch (error) {
        // Don't advance if an error occurred
        this.toastrService.error(this.translationService.get(error.message));
        return;
      }
    }

    this.listIndex++;
    if (this.listIndex > (this.processedTableData.length - 1)) {
      this.onSubmitClick();
    } else {
      this.validationAccepted = false;
      this.showTable = false;
      this.setTableData();
    }
  }

  onBackClick() {
    this.listIndex--;
    if (this.listIndex < 0) {
      this.onCancelClick();
    } else {
      this.validationAccepted = false;
      this.showTable = false;
      this.resultsLoaded = false;
      this.setTableData();
    }
  }

  onSubmitClick() {
    let resultTimestamp: number;
    // Will be undefined if no results or results data
    const previousResults = this.projectCommService.getResults();
    const onResultsLoaded = new EventEmitter<boolean>();
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(onResultsLoaded))
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(success => {
        if (success) {
          this.toastrService.info(this.translationService.get('PROJECT.INITIAL.SIMULATED'));
          const timestamp = this.projectCommService.getResultTimestamp();
          if (resultTimestamp === undefined) {
            resultTimestamp = timestamp;
          }
          // New results are loaded if the timestamps are different or no results were previously loaded
          if (previousResults === undefined || timestamp !== resultTimestamp) {
            this.resultsLoaded = success;
            this.oldResults = false;
            // Process and display results
            this.processResults(this.projectCommService.getResults());
            this.showTable = false;
            this.setTableData();
            this.isSimulating = false;
            onResultsLoaded.next(true);
          }
        }
      });

    // Only simulate if results haven't been loaded yet
    if (this.oldResults) {
      this.isSimulating = true;
      this.toastrService.info(this.translationService.get('PROJECT.INITIAL.SIMULATING'));
      this.projectCommService.simulateProject({
        id: localStorage.getItem('projectID'),
        exchangeRateDate: moment.utc().format('YYYY-MM-DD'),
      });
    } else {
      this.resultsLoaded = true;
      this.showTable = false;
      this.setTableData();
    }
  }

  onCancelClick() {
    this.dialogRef.close(false);
  }

  limitToScreeningProjects() {
    return Utils.limitToScreeningProjects();
  }
}
